import React, {Component, FC} from 'react';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Plural, Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {MessageModel} from '../../../../../../../libs/shared/core/src/models/api/messageModel';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {OkCancelDialog} from '../../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {PermissionCheck} from '../../../../../../../srcCommon/permissions/permissionCheck';
import {useConversationStatusBulkActionPermission} from '../../../../../../../srcCommon/permissions/useBulkActionPermission';
import {
  useConversationStatusActionOfflinePermission,
  useDeleteDraftOfflinePermission,
} from '../../../../../../../srcCommon/permissions/useOfflinePermission';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {combinePermissions, PermissionResult} from '../../../../../../helpers/permissionHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {TriageTeamConfirmationDialogContainer} from '../../topbar/triageTeamConfirmationDialogContainer';
import {BulkActionConfirmationDialog} from '../bulk/bulkActionConfirmationDialog';
import {BulkActionCoordinator} from '../bulk/bulkActionCoordinator';

/*
 * Props.
 */

export enum ConversationsTrashCoordinatorModesEnum {
  TRASH = 'TRASH',
  SPAM = 'SPAM',
  BOTH = 'BOTH',
}

interface ConversationsTrashCoordinatorProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  inboxesParameters: InboxesParameters;
  contextNamespaces: ReadonlyArray<string>;
  draftMessageUids: ReadonlyArray<string>;
  draftMessages: ReadonlyArray<MessageModel>;
  areAllTrashed: boolean;
  areAllSpam: boolean;
  onTrash: () => void;
  onSpam: () => void;
  onReopen: () => void;
  onBulkTrash: () => void;
  onBulkSpam: () => void;
  onBulkReopen: () => void;
  onDeleteMessageClick: (arg: string) => void;
  isBulkActionRequired: boolean;
  shouldShowTrashTeamAlert: boolean;
  shouldShowSpamTeamAlert: boolean;
  mode?: ConversationsTrashCoordinatorModesEnum;

  renderButton: RendererOf<ConversationsTrashButtonProps>;
}

export interface ConversationsTrashButtonProps {
  areAllTrashed: boolean;
  areAllSpam: boolean;
  permissionResult: PermissionResult;
  onClick: () => void;
}

interface PermissionResults {
  offlinePermission: PermissionResult;
  bulkActionPermission: PermissionResult;
}
interface OfflinePermissionCheckProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  children: RendererOf<PermissionResults>;
}

interface DraftOfflinePermissionCheckProps {
  draftMessages: ReadonlyArray<MessageModel>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  children: RendererOf<PermissionResults>;
}

/*
 * Component.
 */

interface ConversationsTrashCoordinatorState {
  isTrashDraftConfirmationOpen: boolean;
  isBulkSpamConfirmationOpen: boolean;
  isTrashTeamDialogOpen: boolean;
  isSpamTeamDialogOpen: boolean;
}

export class ConversationsTrashCoordinator extends Component<
  ConversationsTrashCoordinatorProps,
  ConversationsTrashCoordinatorState
> {
  constructor(props: ConversationsTrashCoordinatorProps) {
    super(props);
    this.state = {
      isTrashDraftConfirmationOpen: false,
      isBulkSpamConfirmationOpen: false,
      isTrashTeamDialogOpen: false,
      isSpamTeamDialogOpen: false,
    };
  }

  /*
   * Event handlers.
   */

  private readonly toggleTrashDraftConfirmationOpen = () => {
    this.setState(({isTrashDraftConfirmationOpen}) => ({
      isTrashDraftConfirmationOpen: !isTrashDraftConfirmationOpen,
    }));
  };

  private readonly toggleBulkSpamConfirmationOpen = () => {
    this.setState(({isBulkSpamConfirmationOpen}) => ({
      isBulkSpamConfirmationOpen: !isBulkSpamConfirmationOpen,
    }));
  };

  private readonly toggleSpamTeamDialogOpen = () => {
    this.setState(({isSpamTeamDialogOpen}) => ({
      isSpamTeamDialogOpen: !isSpamTeamDialogOpen,
    }));
  };
  private readonly toggleTrashTeamDialogOpen = () => {
    this.setState(({isTrashTeamDialogOpen}) => ({
      isTrashTeamDialogOpen: !isTrashTeamDialogOpen,
    }));
  };

  private readonly onTrashRequest = () => {
    const {cellsContext, draftMessageUids, onTrash, shouldShowTrashTeamAlert} = this.props;
    const {inDrafts} = cellsContext;

    // If in the draft folder, and the conversation has drafts, confirm first.
    if (inDrafts && draftMessageUids.length > 0) {
      this.toggleTrashDraftConfirmationOpen();
      return;
    }

    if (shouldShowTrashTeamAlert) {
      this.toggleTrashTeamDialogOpen();
      return;
    }

    // Otherwise, close conversation immediately.
    onTrash();
  };

  private readonly onSpamRequest = () => {
    const {onSpam, isBulkActionRequired, shouldShowSpamTeamAlert} = this.props;
    // If we need to do a bulk action, confirm first.
    if (isBulkActionRequired) {
      this.toggleBulkSpamConfirmationOpen();
      return;
    }

    if (shouldShowSpamTeamAlert) {
      this.toggleSpamTeamDialogOpen();
      return;
    }

    // Otherwise, close open immediately.
    onSpam();
  };

  private readonly onBulkSpamConfirm = () => {
    this.toggleBulkSpamConfirmationOpen();
    this.props.onBulkSpam();
  };

  private readonly onTrashDraftConfirm = () => {
    this.toggleTrashDraftConfirmationOpen();

    const {draftMessageUids, onDeleteMessageClick} = this.props;
    draftMessageUids.forEach((uid) => onDeleteMessageClick(uid));
  };

  /*
   * Render.
   */

  render() {
    const {
      contextNamespaces,
      cellsContext,
      conversations,
      draftMessages,
      inboxesParameters,
      isBulkActionRequired,
    } = this.props;

    // The trash button is repurposed to delete drafts, which only needs an offline permissions check.
    if (cellsContext.inDrafts) {
      return (
        <DraftOfflinePermissionCheck
          draftMessages={draftMessages}
          inboxesParameters={inboxesParameters}
          isBulkActionRequired={isBulkActionRequired}
        >
          {({offlinePermission, bulkActionPermission}) =>
            this.renderButton(combinePermissions(offlinePermission, bulkActionPermission))
          }
        </DraftOfflinePermissionCheck>
      );
    }

    return (
      <OfflinePermissionCheck
        cellsContext={cellsContext}
        conversations={conversations}
        inboxesParameters={inboxesParameters}
        isBulkActionRequired={isBulkActionRequired}
      >
        {({offlinePermission, bulkActionPermission}) => (
          <PermissionCheck
            namespaces={contextNamespaces}
            permission={PermissionsEnum.CONVERSATION_TRASH}
            offlinePermission={offlinePermission}
            bulkActionPermission={bulkActionPermission}
          >
            {(permission) => this.renderButton(permission)}
          </PermissionCheck>
        )}
      </OfflinePermissionCheck>
    );
  }

  renderButton(permissionResult: PermissionResult) {
    const {areAllTrashed, areAllSpam, onReopen, onBulkReopen, onBulkTrash, isBulkActionRequired} = this.props;
    const mode = this.props.mode ?? ConversationsTrashCoordinatorModesEnum.BOTH;

    if (!permissionResult.isAllowed || areAllSpam || areAllTrashed) {
      return (
        <BulkActionCoordinator
          actionType={BulkActionTypesEnum.REOPEN}
          onNormalAction={onReopen}
          onBulkAction={onBulkReopen}
          shouldPerformBulkAction={isBulkActionRequired}
        >
          {(onClick) =>
            this.props.renderButton({
              permissionResult,
              areAllTrashed: mode === ConversationsTrashCoordinatorModesEnum.SPAM ? false : areAllTrashed,
              areAllSpam: mode === ConversationsTrashCoordinatorModesEnum.TRASH ? false : areAllSpam,
              onClick,
            })
          }
        </BulkActionCoordinator>
      );
    }

    const keyHandlers =
      mode === ConversationsTrashCoordinatorModesEnum.TRASH
        ? {}
        : {[KeyboardActionsEnum.SPAM]: this.onSpamRequest};
    const onNormalAction =
      mode === ConversationsTrashCoordinatorModesEnum.SPAM ? this.onSpamRequest : this.onTrashRequest;
    const onBulkAction =
      mode === ConversationsTrashCoordinatorModesEnum.SPAM ? this.onSpamRequest : onBulkTrash;
    return (
      <KeyboardShortcuts handlers={keyHandlers}>
        <BulkActionCoordinator
          actionType={BulkActionTypesEnum.TRASH}
          onNormalAction={onNormalAction}
          onBulkAction={onBulkAction}
          shouldPerformBulkAction={isBulkActionRequired}
        >
          {(onClick) =>
            this.props.renderButton({
              permissionResult,
              areAllTrashed: mode === ConversationsTrashCoordinatorModesEnum.SPAM ? false : areAllTrashed,
              areAllSpam: mode === ConversationsTrashCoordinatorModesEnum.TRASH ? false : areAllSpam,
              onClick,
            })
          }
        </BulkActionCoordinator>
        {this.maybeRenderDialogs()}
      </KeyboardShortcuts>
    );
  }

  private maybeRenderDialogs() {
    if (this.state.isBulkSpamConfirmationOpen) {
      return this.renderBulkSpamConfirmation();
    }

    if (this.state.isTrashDraftConfirmationOpen) {
      return this.renderTrashDraftDialog();
    }

    if (this.state.isTrashTeamDialogOpen) {
      return this.renderTrashTeamDialog();
    }

    if (this.state.isSpamTeamDialogOpen) {
      return this.renderSpamTeamDialog();
    }

    return null;
  }

  private renderBulkSpamConfirmation() {
    return (
      <BulkActionConfirmationDialog
        onConfirmClick={this.onBulkSpamConfirm}
        onRequestClose={this.toggleBulkSpamConfirmationOpen}
        actionType={BulkActionTypesEnum.SPAM}
      />
    );
  }

  private renderTrashDraftDialog() {
    const {draftMessageUids} = this.props;
    return (
      <OkCancelDialog
        title={
          <Plural
            id="toolbar-deletedrafts-confirm-title"
            value={draftMessageUids.length}
            one="Delete draft"
            other="Delete # drafts"
          />
        }
        okButtonLabel={<Trans id="toolbar-deletedrafts-confirm-ok">Delete</Trans>}
        onOkClick={this.onTrashDraftConfirm}
        onRequestClose={this.toggleTrashDraftConfirmationOpen}
      >
        <Plural
          id="toolbar-deletedrafts-confirm-content"
          value={draftMessageUids.length}
          one={
            <>
              This draft will be deleted permanently.
              <br />
              This <strong>cannot</strong> be undone.
            </>
          }
          other={
            <>
              These drafts will be deleted permanently.
              <br />
              This <strong>cannot</strong> be undone.
            </>
          }
        />
      </OkCancelDialog>
    );
  }

  private renderTrashTeamDialog() {
    return (
      <TriageTeamConfirmationDialogContainer
        cellsContext={this.props.cellsContext}
        conversations={this.props.conversations}
        onConfirmClick={() => {
          this.props.onTrash();
          this.toggleTrashTeamDialogOpen();
        }}
        onRequestClose={this.toggleTrashTeamDialogOpen}
        action={TriageActionsEnum.TRASH}
      />
    );
  }

  private renderSpamTeamDialog() {
    return (
      <TriageTeamConfirmationDialogContainer
        cellsContext={this.props.cellsContext}
        conversations={this.props.conversations}
        onConfirmClick={() => {
          this.props.onSpam();
          this.toggleSpamTeamDialogOpen();
        }}
        onRequestClose={this.toggleSpamTeamDialogOpen}
        action={TriageActionsEnum.SPAM}
      />
    );
  }
}

const OfflinePermissionCheck: FC<OfflinePermissionCheckProps> = (props) => {
  const offlinePermission = useConversationStatusActionOfflinePermission(
    props.cellsContext,
    props.conversations,
  );
  const bulkActionPermission = useConversationStatusBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  return <>{props.children({offlinePermission, bulkActionPermission})}</>;
};

const DraftOfflinePermissionCheck: FC<DraftOfflinePermissionCheckProps> = (props) => {
  const offlinePermission = useDeleteDraftOfflinePermission(props.draftMessages);
  const bulkActionPermission = useConversationStatusBulkActionPermission(
    props.isBulkActionRequired,
    props.inboxesParameters,
  );

  return <>{props.children({offlinePermission, bulkActionPermission})}</>;
};
